import React, { useContext } from 'react'
import { themeGet } from '@styled-system/theme-get'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'
import { textStyle as textStyleVariant } from '@zuto/utilities'
import { TextStyle } from '@zuto/types'
import Hint from './Hint'
import MuiHint from './MuiHint'
import { ApplicationFormContext } from '../../../../src/components/ApplicationForm/ApplicationFormContext'

export interface QuestionProps extends React.HTMLAttributes<HTMLDivElement>, SpaceProps {
  /** When active, this renders a Hint button onto the page alongside the question.
   * The content is displayed via an animated slide-down toggle. */
  hintText?: string
  hintOnClick?: () => void
  textStyle?: TextStyle
  showHintByDefault?: boolean
  /** When provided, renders a text-based trigger instead of the default icon button */
  hintLabel?: string
}

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${space}
`

const StyledText = styled.h2<{ textStyle?: TextStyle }>`
  ${themeGet('questionsAndHints.questions')};
  flex: 1;
  ${space({ mr: 4 })}
  ${textStyleVariant}
`

/**
 * This component renders a Question onto the page.
 * The Question and Hint text are both customisable. <br/>
 * The Hint is optional, meaning you can render a Question alone on a page.<br/><br/>
 * When the Hint is visible, the padding and margin are removed so that the animation
 * plays smoothly and the surrounding text is accounted for.<br/>
 * The base animation is using CSS. The post-animation property changes are via JavaScript.
 *
 */
const Question = ({ children, hintText, hintOnClick, showHintByDefault = false, textStyle, hintLabel, ...props }: QuestionProps): JSX.Element => {
  const { featureSwitches } = useContext(ApplicationFormContext)
  const { msmThemeV1 } = featureSwitches ?? {}
  return (
    <Wrapper {...props}>
      <StyledText textStyle={textStyle}>{children}</StyledText>
      {hintText && !msmThemeV1 && (
        <Hint
          showHintByDefault={showHintByDefault}
          onClick={hintOnClick}
          hintLabel={hintLabel}
        >
          {hintText}
        </Hint>
      )}
      {hintText && msmThemeV1 && (
        <MuiHint
          showHintByDefault={showHintByDefault}
          onClick={hintOnClick}
          hintLabel={hintLabel}
        >
          {hintText}
        </MuiHint>
      )}
    </Wrapper>
  )
}

export default Question
